import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import isString from 'lodash/isString';

import type {
  GridFilterItem,
  GridFilterModel,
  GridInitialState,
} from '@packages/fenrir/components';

import { ASSETS_DATA_GRID_STATE_KEY } from '../../../../components/assets-table/asset-table.constants';
import { getParam } from '../../../../filter-management/filter-fixtures';
import type { AssetType } from '../../../asset-type';
import { defaultGridFiltersByType } from '../asset-type-fields-filters';
import type { Filters } from '../filters';
import { isFilterEmpty } from '../is-filter-empty/is-filter-empty';

import type {
  FilterMapItem,
  FilterParamKey,
  FilterQueryParamType,
} from './filter-map';
import { filterMap } from './filter-map';

const defaultQueryParams: FilterQueryParamType = {
  lastScanFrom: null,
  lastScanTo: null,
  lastSeenFrom: null,
  lastSeenTo: null,
  categories: [],
  importances: [],
  riskScore: null,
  sources: [],
  tags: [],
  name: '',
  hostname: '',
  os: '',
  mac: '',
  ipAddress: '',
};

export const formatParam = <T>(value: T | undefined | null) =>
  isFilterEmpty(value) ? undefined : value;

/**
 * Converts the query params to the format used by the filter model
 * @param queryParams - The query params to convert.
 * @returns The formatted query params
 * @example
 * // Example with asset category and risk score filters
 * getFormattedQueryParams({
 *   categories: ['unassigned'],
 *   riskScore: [3, 10],
 * });
 * });
 */
export const getFormattedQueryParams = (
  queryParams: Partial<FilterQueryParamType>,
): FilterQueryParamType => {
  const {
    lastScanFrom,
    lastScanTo,
    lastSeenFrom,
    lastSeenTo,
    riskScore,
    categories,
    importances,
    sources,
    tags,
    name,
    hostname,
    os,
    mac,
    ipAddress,
  } = queryParams;
  return {
    categories: formatParam(categories),
    importances: formatParam(importances),
    lastScanFrom: lastScanFrom ?? null,
    lastScanTo: lastScanTo ?? null,
    lastSeenFrom: lastSeenFrom ?? null,
    lastSeenTo: lastSeenTo ?? null,
    riskScore: formatParam(riskScore),
    sources: formatParam(sources),
    tags: formatParam(tags),
    name: formatParam(name),
    hostname: formatParam(hostname),
    os: formatParam(os),
    mac: formatParam(mac),
    ipAddress: formatParam(ipAddress),
  };
};

/**
 * Converts the filter model to the query params format
 * @param filterModel - The filter model received from data grid to convert.
 * @returns The default query parameters in case the generated query params are empty, otherwise the new query params object
 * @example
 * // Example with asset category and risk score filters
 * getQueryParamsFromFilterModel({
 *   items: [
 *    {field : 'categories', operator: 'isAnyOf', value: ['unassigned']},
 *    {field : 'risk.score', operator: 'isAnyOf', value: [3, 10]},
 *   ],
 * });
 */

export const getQueryParamsFromFilterModel = (filterModel: GridFilterModel) => {
  let updatedQueryParams: Partial<FilterQueryParamType> = {};

  filterMap.forEach((filterItem) => {
    const filterValue = filterItem.getValue(
      getParam(filterItem.column, filterModel, filterItem.operator),
    );

    // Number 0 is a valid value
    if (filterValue || typeof filterValue === 'number') {
      if (Array.isArray(filterValue) && Array.isArray(filterItem.param)) {
        filterItem.param.forEach((param, index) => {
          updatedQueryParams = {
            ...updatedQueryParams,
            [param]: filterValue[index],
          };
        });
      } else {
        updatedQueryParams = {
          ...updatedQueryParams,
          [filterItem.param as FilterParamKey]: filterValue,
        };
      }
    }
  });

  const result = !isEmpty(updatedQueryParams)
    ? updatedQueryParams
    : defaultQueryParams;
  return getFormattedQueryParams(result);
};

/**
 * Convert the range filter query params to the new filter model structure
 * If existing filter model item is found, update the value
 * If not found, create a new filter model item with the range value
 * @param value - the filter value to be applied (e.g., a number for risk score)
 * @param key - the specific filter parameter key (e.g., "lastScanStart")
 * @param gridFilterModel - current filter model
 * @param filterMapItem - filter item (e.g., {"column": "lastScanStart", "operator": isAnyOf, "param": ["lastScanStart", "lastScanEnd"], getValue: () => {}} )
 * @returns the updated filter model with the converted range filter applied
 */
const convertRangeParams = (
  value: any,
  key: FilterParamKey,
  gridFilterModel: GridFilterModel,
  filterMapItem: FilterMapItem,
) => {
  const toUpdateIndex = filterMapItem.param.indexOf(key);
  if (toUpdateIndex === -1) return gridFilterModel;

  const gridFilterItemIndex = gridFilterModel.items.findIndex(
    (item) => item.field === filterMapItem.column,
  );

  if (gridFilterItemIndex !== -1) {
    const updatedItems = [...gridFilterModel.items];
    const updatedValue = [
      ...(updatedItems[gridFilterItemIndex].value ?? [undefined, undefined]),
    ];
    updatedValue[toUpdateIndex] = value;

    updatedItems[gridFilterItemIndex] = {
      ...updatedItems[gridFilterItemIndex],
      value: updatedValue,
    };

    return {
      ...gridFilterModel,
      items: updatedItems,
    };
  }

  const newItem: GridFilterItem = {
    value: [undefined, undefined],
    field: filterMapItem.column,
    operator: filterMapItem.operator,
    id: gridFilterModel.items.length + 1,
  };

  newItem.value[toUpdateIndex] = value;

  return {
    ...gridFilterModel,
    items: [...gridFilterModel.items, newItem],
  };
};

/**
 * Converts the url query params to the filter model.
 * @param queryParams - The query params to convert.
 * @param assetType - The asset type to use for the default filters.
 * @returns The filter model.
 * @example
 * // Example with category filter
 * getInitialFilterModel({ category: 'unassigned' }, 'device');
 *
 * // Example with multiple filters
 * getInitialFilterModel({
 *   category: 'unassigned',
 *   lastScanFrom: '2023-01-01',
 *   lastScanTo: '2023-01-02',
 *   riskScore: [0, 10],
 * }, 'device');
 *
 * Filter model structure details - https://mui.com/x/api/data-grid/grid-filter-model/
 */
export const getInitialFilterModel = (
  queryParams: Partial<FilterQueryParamType>,
  assetType: AssetType,
) => {
  if (queryParams === null) {
    return getInitialFilterState(assetType, {
      items: defaultGridFiltersByType[assetType],
    });
  }
  // check if we have any valid query params to use as defaults for filters
  const initialFilterState: GridFilterModel = Object.entries(
    queryParams,
  ).reduce(
    (gridFilterModel: GridFilterModel, [key, value]) => {
      if (isEmpty(value)) {
        return gridFilterModel;
      }

      const filterItem = filterMap.find((filter) => {
        if (Array.isArray(filter.param)) {
          return filter.param.includes(key as FilterParamKey);
        }
        return filter.param === key;
      });
      if (!filterItem) return gridFilterModel; // something in the URL we don't support

      if (Array.isArray(filterItem.param)) {
        return convertRangeParams(
          value,
          key as FilterParamKey,
          gridFilterModel,
          filterItem,
        );
      }

      return {
        ...gridFilterModel,
        items: [
          ...gridFilterModel.items,
          {
            value,
            field: filterItem.column,
            operator: filterItem.operator,
            id: gridFilterModel.items.length + 1,
          },
        ],
      };
    },
    { items: [] },
  );

  if (initialFilterState?.items?.length > 0) return initialFilterState;

  // fallback to using session storage for defaults, else we will use preset defaults
  return getInitialFilterState(assetType, {
    items: defaultGridFiltersByType[assetType],
  });
};

/**
 * Get the initial filter state from session storage or use the default filters
 * @param defaultFilters - The default filters to use if no state is found in session storage.
 * @returns The initial filter state.
 * @example
 * getInitialFilterState({ items: defaultGridFiltersByType.device });
 * // returns the default filters for device if no state is found in session storage
 */
export const getInitialFilterState = (
  assetType: AssetType,
  defaultFilters: GridFilterModel,
): GridFilterModel => {
  try {
    const stateFromSessionStorage = sessionStorage.getItem(
      ASSETS_DATA_GRID_STATE_KEY,
    );

    if (!stateFromSessionStorage) {
      return defaultFilters;
    }

    const gridState = JSON.parse(stateFromSessionStorage) as Record<
      AssetType,
      GridInitialState
    >;

    if (!gridState || typeof gridState !== 'object') {
      return defaultFilters;
    }

    const filterModel = gridState[assetType]?.filter?.filterModel;

    if (!filterModel || !Array.isArray(filterModel.items)) {
      return defaultFilters;
    }

    return filterModel;
  } catch {
    return defaultFilters;
  }
};

/**
 * Converts the query params to the API filters format
 * @param queryParams - The query params to convert.
 * @returns The API filters in the format expected by backend.
 * @example
 * // Example with asset category and risk score filters
 * getAPIFiltersFromQueryParams({
 *   categories: ['unassigned'],
 *   riskScore: [3, 10],
 * });
 *
 * output:
 * {
 *   "deletedTime": "",
 *   "hostname": "",
 *   "importances": [],
 *   "ipAddress": "",
 *   "lastScan": [null, null],
 *   "lastSeen": [null, null],
 *   "mac": "",
 *   "name": "",
 *   "os": "",
 *   "riskScore": [3, 10],
 *   "sources": [],
 *   "tags": []
 * }
 */
export const getAPIFiltersFromQueryParams = (
  queryParams: Partial<FilterQueryParamType>,
) => {
  const [riskScoreFrom, riskScoreTo] = queryParams?.riskScore ?? [];
  const {
    lastScanFrom,
    lastScanTo,
    lastSeenFrom,
    lastSeenTo,
    categories,
    importances,
    sources,
    tags,
    name,
    hostname,
    os,
    mac,
    ipAddress,
  } = queryParams;

  const riskScore: Filters['riskScore'] =
    !isNil(riskScoreFrom) && !isNil(riskScoreTo)
      ? [riskScoreFrom, riskScoreTo]
      : null;

  const lastScan: Filters['lastScan'] = [
    lastScanFrom ?? null,
    lastScanTo ?? null,
  ];

  const lastSeen: Filters['lastSeen'] = [
    lastSeenFrom ?? null,
    lastSeenTo ?? null,
  ];

  return {
    lastScan,
    lastSeen,
    categories: categories ?? [],
    importances: importances ?? [],
    riskScore,
    sources: sources ?? [],
    tags: tags?.filter(isString) ?? [],
    name: name ?? '',
    hostname: hostname ?? '',
    os: os ?? '',
    mac: mac ?? '',
    ipAddress: ipAddress ?? '',
    deletedTime: '',
  };
};
